if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-modal');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer'))
          this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Apply customer tier multiplier (diamond/platinum/gold/silver) to the
        // giveaway entry property so the per-line entries stored on the cart
        // item already include the multiplier.
        const tierMultiplier = parseInt(window.entries?.customer_tier_multiplier, 10);
        const giveawayName = window.entries?.giveaway_name;
        if (giveawayName && !Number.isNaN(tierMultiplier) && tierMultiplier > 1) {
          const propKey = `properties[${giveawayName}]`;
          const rawValue = formData.get(propKey);
          if (rawValue != null) {
            const base = parseInt(String(rawValue).replace(/,/g, ''), 10);
            if (!Number.isNaN(base)) {
              formData.set(propKey, String(base * tierMultiplier));
            }
          }
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              await this.syncShippingProtection([]);
              if (typeof window.syncCartEntries === 'function') {
                await window.syncCartEntries();
              }
              window.location = window.routes.cart_url;
              return;
            }

            // ── Wait for SP to be added BEFORE rendering the drawer ──────────
            // getSectionsToRender() ids are passed so SP's /cart/add.js response
            // includes fresh drawer HTML. If SP was just added we swap in its
            // sections so the drawer opens showing SP already in the cart.
            const sectionIds = this.cart
              ?.getSectionsToRender?.()
              ?.map((s) => s.id) ?? [];

            const spResponse = await this.syncShippingProtection(sectionIds);

            if (typeof window.syncCartEntries === 'function') {
              await window.syncCartEntries();
            }

            // SP response has more up-to-date sections (cart now has SP in it)
            const finalResponse = spResponse
              ? { ...response, sections: spResponse.sections }
              : response;

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            finalResponse._addedQuantity = formData.get('quantity') || 1;

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: finalResponse,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(finalResponse);
                    });
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(finalResponse);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty'))
              this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      // ── syncShippingProtection ───────────────────────────────────────────
      // Checks if SP is already in cart. If not, adds it and returns the
      // /cart/add.js response (which includes fresh `sections` HTML).
      // Returns null if SP is disabled, already in cart, or on error.
      //
      // @param {string[]} sectionIds  — section IDs to request from Shopify
      //                                 (same ones CartDrawer needs to render)
      async syncShippingProtection(sectionIds = []) {
        const sp = window.shippingProtection;
        if (!sp?.status || !sp?.product?.variant_id) return null;

        try {
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();

          const alreadyInCart = cart.items.some(
            (item) => item.variant_id === sp.product.variant_id
          );
          if (alreadyInCart) return null;

          // Add SP and request the same drawer sections so the response
          // can be handed directly to CartDrawer.renderContents()
          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: sp.product.variant_id,
              quantity: 1,
              sections: sectionIds,
              sections_url: window.location.pathname,
            }),
          });

          if (!addResponse.ok) return null;

          return await addResponse.json(); // { id, sections: { ... } }
        } catch (e) {
          console.error('[SP] Sync failed:', e);
          return null;
        }
      }
    },
  );
}